/**
 * Generated by orval v8.5.3 🍺
 * Do not edit manually.
 * Metapic Social Media API Documentation
 * <p>Welcome to the API documentation for integrating with Metapic's Social Media Platform.</p>
            <h2>Authentication</h2>
            <p>This API uses <strong>Bearer Token</strong> authentication. You must include a valid token in the <strong>Authorization</strong> header with every request to access protected endpoints.</p>
            <h4>Example:</h4>
            <pre>Authorization: Bearer <i>your_token_here</i></pre>
            <p>*Replace <i>your_token_here</i> with your actual Bearer token. All API requests require this token for authentication.</p>
 * OpenAPI spec version: 1.0
 */
import * as axios from 'axios'
import type { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios'

import type {
  AssignContentItemCampaignRequest,
  ChangeContentStatusDto,
  ContentImportResponse,
  ContentItemAssetDownloadUrl,
  GetLinkDetailsParams,
  ImportContentRequest,
  LinkDetails,
  ListContentItems200,
  ListContentItemsParams,
  UploadAsset201,
  UploadDto,
} from '../metapicSocialMediaAPIDocumentation.schemas'

export const getContentLibrary = (axiosInstance: AxiosInstance = axios.default) => {
  /**
   * Retrieve a paginated list of content items with optional filters
   * @summary List content items
   */
  const listContentItems = (
    params?: ListContentItemsParams,
    options?: AxiosRequestConfig,
  ): Promise<AxiosResponse<ListContentItems200>> => {
    return axiosInstance.get(`/v1/content-library`, {
      ...options,
      params: { ...params, ...options?.params },
    })
  }
  const getLinkDetails = (
    itemId: string,
    params?: GetLinkDetailsParams,
    options?: AxiosRequestConfig,
  ): Promise<AxiosResponse<LinkDetails>> => {
    return axiosInstance.get(`/v1/content-library/${itemId}/link-details`, {
      ...options,
      params: { ...params, ...options?.params },
    })
  }
  /**
   * Returns a short-lived presigned url that serves the asset as an attachment. It is always distinct from the playback url in the asset response, so the browser cannot reuse a cached opaque playback response for the CORS download request.
   * @summary Get a download url for a content item asset
   */
  const getAssetDownloadUrl = (
    itemId: string,
    assetId: string,
    options?: AxiosRequestConfig,
  ): Promise<AxiosResponse<ContentItemAssetDownloadUrl>> => {
    return axiosInstance.get(
      `/v1/content-library/${itemId}/assets/${assetId}/download-url`,
      options,
    )
  }
  /**
   * Approve or reject a content item
   * @summary Change content item status
   */
  const changeContentStatus = (
    id: string,
    changeContentStatusDto: ChangeContentStatusDto,
    options?: AxiosRequestConfig,
  ): Promise<AxiosResponse<void>> => {
    return axiosInstance.patch(`/v1/content-library/${id}/status`, changeContentStatusDto, options)
  }
  /**
   * Admin only. Re-assigning a previously removed campaign restores it.
   * @summary Assign a campaign to a content item
   */
  const assignCampaign = (
    itemId: string,
    assignContentItemCampaignRequest: AssignContentItemCampaignRequest,
    options?: AxiosRequestConfig,
  ): Promise<AxiosResponse<void>> => {
    return axiosInstance.post(
      `/v1/content-library/${itemId}/campaigns`,
      assignContentItemCampaignRequest,
      options,
    )
  }
  /**
   * Admin only. The relation is marked removed rather than deleted, so ingestion will not re-add it.
   * @summary Remove a campaign from a content item
   */
  const removeCampaign = (
    itemId: string,
    campaignId: number,
    options?: AxiosRequestConfig,
  ): Promise<AxiosResponse<void>> => {
    return axiosInstance.delete(`/v1/content-library/${itemId}/campaigns/${campaignId}`, options)
  }
  const uploadAsset = (
    uploadDto: UploadDto,
    options?: AxiosRequestConfig,
  ): Promise<AxiosResponse<UploadAsset201>> => {
    return axiosInstance.post(`/v1/content-library/upload`, uploadDto, options)
  }
  /**
   * Admin only. Ingests one Instagram post or reel and attaches it to the given advertiser and campaign. Waits up to 60 seconds; on 202 repeat the same request to keep waiting.
   * @summary Import a content item from its public url
   */
  const importContent = (
    importContentRequest: ImportContentRequest,
    options?: AxiosRequestConfig,
  ): Promise<AxiosResponse<ContentImportResponse>> => {
    return axiosInstance.post(`/v1/content-library/import`, importContentRequest, options)
  }
  return {
    listContentItems,
    getLinkDetails,
    getAssetDownloadUrl,
    changeContentStatus,
    assignCampaign,
    removeCampaign,
    uploadAsset,
    importContent,
  }
}
export type ListContentItemsResult = AxiosResponse<ListContentItems200>
export type GetLinkDetailsResult = AxiosResponse<LinkDetails>
export type GetAssetDownloadUrlResult = AxiosResponse<ContentItemAssetDownloadUrl>
export type ChangeContentStatusResult = AxiosResponse<void>
export type AssignCampaignResult = AxiosResponse<void>
export type RemoveCampaignResult = AxiosResponse<void>
export type UploadAssetResult = AxiosResponse<UploadAsset201>
export type ImportContentResult = AxiosResponse<ContentImportResponse>
